import { useEffect, useState } from 'react'
import {
  login, normalizePhone, requestCode, resetPassword, signup, verifyCode,
  type SignupInput,
} from '../db/auth'
import {
  AIDE_IDENTIFIANT, AIDE_TELEPHONE, MAX_NOM, MIN_MOT_DE_PASSE, MOTIF_IDENTIFIANT,
  MOTIF_TELEPHONE, formaterIdentifiant, formaterTelephone,
  messageEmail, messageIdentifiant, messageMotDePasse, messageTelephone,
} from '../lib/limites'
import { Icon } from './Icon'
import './AuthGate.css'

/**
 * L'entrée dans Ariariko — décision 2026-08-24.
 *
 * Le SEUL moment où l'app exige le réseau. Une fois le téléphone prouvé, le
 * jeton reste sur l'appareil : rouvrir l'app ne redemande jamais de se
 * connecter, et tout redevient utilisable hors ligne.
 *
 * ⚠️ Ce blocage inverse la décision fondatrice « aucun compte n'est requis ».
 * Il est assumé, mais il a un prix : quelqu'un qui installe l'app sans réseau
 * ne peut rien faire. L'écran le dit franchement plutôt que de tourner dans le
 * vide — un écran qui charge sans fin se lit comme une app cassée.
 */
type Etape = 'accueil' | 'inscription' | 'connexion' | 'code' | 'oubli' | 'nouveauMdp'

/**
 * Le serveur reconnaît le téléphone ou l'email au même endroit — un `@` dans
 * l'identifiant. On rejoue exactement la même règle ici : lui envoyer un
 * canal différent de celui qu'il aurait déduit lui-même enverrait le code au
 * mauvais endroit.
 */
function canalDe(identifiant: string): 'sms' | 'email' {
  return identifiant.includes('@') ? 'email' : 'sms'
}

/**
 * Les mêmes délais que `Otp::Issue::COOLDOWNS` côté serveur (décision
 * 2026-09-05) : le SMS se paie, l'email non — d'où l'écart. Dupliqués ici
 * plutôt que demandés au serveur : ce n'est qu'un affichage, la seule
 * autorité qui compte reste l'API, qui refuse de toute façon un renvoi
 * prématuré.
 */
const COOLDOWN_MS: Record<'sms' | 'email', number> = {
  sms: 20 * 60_000,
  email: 5 * 60_000,
}

/** « 19:47 » — le format qu'on lit d'un coup d'œil sur un bouton désactivé. */
function formatCompteARebours(ms: number): string {
  const secondesTotal = Math.ceil(ms / 1000)
  const minutes = Math.floor(secondesTotal / 60)
  const secondes = secondesTotal % 60
  return `${minutes}:${secondes.toString().padStart(2, '0')}`
}

export function AuthGate({ onDone }: { onDone: () => void }) {
  const [etape, setEtape] = useState<Etape>('accueil')
  const [erreur, setErreur] = useState<string | null>(null)
  const [envoi, setEnvoi] = useState(false)

  const [form, setForm] = useState<SignupInput>({
    first_name: '', last_name: '', birthdate: '', phone: '', password: '', email: '',
  })
  const [identifiant, setIdentifiant] = useState('')
  const [motDePasse, setMotDePasse] = useState('')
  const [code, setCode] = useState('')
  const [nouveauMotDePasse, setNouveauMotDePasse] = useState('')

  // L'instant (epoch ms) où « Renvoyer le code » redevient cliquable. `null`
  // hors des étapes qui ont un bouton de renvoi.
  const [renvoiApres, setRenvoiApres] = useState<number | null>(null)
  const [maintenant, setMaintenant] = useState(() => Date.now())

  // Ne tique QUE pendant un compte à rebours réel : sans cette garde, chaque
  // écran de l'app tournerait un intervalle pour rien.
  useEffect(() => {
    if (renvoiApres === null) return
    const id = setInterval(() => setMaintenant(Date.now()), 1000)
    return () => clearInterval(id)
  }, [renvoiApres])

  const resteAvantRenvoi = renvoiApres !== null ? Math.max(0, renvoiApres - maintenant) : 0

  function demarrerCooldown(channel: 'sms' | 'email') {
    // Les deux à partir du MÊME instant : `maintenant` datait du montage du
    // composant, pas de l'envoi. Sans ce réalignement, le premier affichage
    // du compte à rebours incluait tout le temps écoulé depuis l'ouverture de
    // l'écran — un « 20:04 » au lieu d'un « 20:00 » bien rond.
    const instant = Date.now()
    setMaintenant(instant)
    setRenvoiApres(instant + COOLDOWN_MS[channel])
  }

  const champ = (cle: keyof SignupInput) => ({
    value: form[cle] ?? '',
    onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
      setForm((f) => ({ ...f, [cle]: event.target.value })),
  })

  async function inscrire(event: React.FormEvent) {
    event.preventDefault()
    setErreur(null)

    // Vérifié ICI aussi, et pas seulement côté serveur : un aller-retour réseau
    // pour apprendre qu'on a mal tapé son numéro est un aller-retour de trop
    // quand la connexion se paie à la mégaoctet.
    const faute = messageTelephone(form.phone, { obligatoire: true })
      ?? messageMotDePasse(form.password)
      ?? (form.email?.trim() ? messageEmail(form.email) : null)
    if (faute) return setErreur(faute)

    setEnvoi(true)
    const result = await signup({
      ...form,
      phone: normalizePhone(form.phone),
      email: form.email?.trim() || undefined,
    })
    setEnvoi(false)

    if (!result.ok) return setErreur(result.error)
    // Le SMS n'est pas branché en prod (Delivery::Console) : un email fourni
    // est le SEUL canal réellement livré. Le serveur ouvre désormais une
    // session sur l'un ou l'autre canal prouvé (décision 2026-09-06), donc on
    // bascule dessus plutôt que de coincer l'utilisateur sur un code qui
    // n'arrivera jamais.
    const canal = form.email?.trim() ? form.email.trim() : normalizePhone(form.phone)
    setIdentifiant(canal)
    setCode('')
    demarrerCooldown(canalDe(canal))
    setEtape('code')
  }

  /**
   * L'entrée ordinaire — décision 2026-08-24 (soir).
   *
   * Aucun SMS : un code par connexion coûterait de l'argent à chaque ouverture
   * de l'app, et exigerait du réseau là où Ariariko promet de s'en passer.
   */
  async function seConnecter(event: React.FormEvent) {
    event.preventDefault()
    setErreur(null)

    const faute = messageIdentifiant(identifiant, { obligatoire: true })
    if (faute) return setErreur(faute)

    setEnvoi(true)
    const result = await login(identifiant, motDePasse)
    setEnvoi(false)

    if (!result.ok) return setErreur(result.error)
    if (result.data.session) return onDone()

    // Le mot de passe était bon, mais le numéro n'a jamais été prouvé : le
    // serveur a envoyé un code, on enchaîne dessus sans rien redemander.
    setErreur('Ton numéro doit encore être confirmé. Un code vient d’être demandé.')
    setCode('')
    demarrerCooldown('sms')
    setEtape('code')
  }

  async function verifier(event: React.FormEvent) {
    event.preventDefault()
    setErreur(null)
    setEnvoi(true)
    const result = await verifyCode(identifiant, code, canalDe(identifiant))
    setEnvoi(false)

    if (!result.ok) return setErreur(result.error)
    if (!result.data.session) {
      // N'arrive plus qu'ici : aucun des deux canaux n'a jamais été prouvé
      // (cas normalement impossible juste après une inscription réussie).
      return setErreur("Ce canal est confirmé, mais aucune session n'a pu s'ouvrir.")
    }
    onDone()
  }

  async function renvoyer() {
    setErreur(null)
    const result = await requestCode(identifiant, canalDe(identifiant))
    if (!result.ok) return setErreur(result.error)
    setErreur('Un nouveau code a été demandé.')
    demarrerCooldown(canalDe(identifiant))
  }

  /** Mot de passe oublié, étape 1 : demander un code sur le canal tapé. */
  async function demanderReinitialisation(event: React.FormEvent) {
    event.preventDefault()
    setErreur(null)

    const faute = messageIdentifiant(identifiant, { obligatoire: true })
    if (faute) return setErreur(faute)

    setEnvoi(true)
    const result = await requestCode(identifiant, canalDe(identifiant))
    setEnvoi(false)

    // Réponse identique que le compte existe ou non — voir `auth/code` côté
    // serveur : sinon l'écran devient un annuaire de qui est inscrit.
    if (!result.ok) return setErreur(result.error)
    setCode('')
    setNouveauMotDePasse('')
    demarrerCooldown(canalDe(identifiant))
    setEtape('nouveauMdp')
  }

  async function renvoyerPourReinitialiser() {
    setErreur(null)
    const result = await requestCode(identifiant, canalDe(identifiant))
    if (!result.ok) return setErreur(result.error)
    setErreur('Un nouveau code a été demandé.')
    demarrerCooldown(canalDe(identifiant))
  }

  /** Mot de passe oublié, étape 2 : le code prouve le droit de le remplacer. */
  async function reinitialiser(event: React.FormEvent) {
    event.preventDefault()
    setErreur(null)

    const faute = messageMotDePasse(nouveauMotDePasse)
    if (faute) return setErreur(faute)

    setEnvoi(true)
    const result = await resetPassword(identifiant, code, nouveauMotDePasse)
    setEnvoi(false)

    if (!result.ok) return setErreur(result.error)
    if (result.data.session) return onDone()

    // Le mot de passe a bien changé côté serveur — seule la session attend
    // encore la preuve du téléphone, jamais prouvée sur ce compte-là.
    setErreur('Mot de passe changé. Il reste à confirmer ton numéro : un code vient d’être envoyé par SMS.')
    setCode('')
    demarrerCooldown('sms')
    setEtape('code')
  }

  return (
    <div className="auth">
      <div className="auth-card">
        <header className="auth-head">
          <h1>Ariariko</h1>
          <p>Gère ton argent, simplement.</p>
        </header>

        {erreur && <p className="auth-error" role="alert">{erreur}</p>}

        {etape === 'accueil' && (
          <div className="auth-choices">
            <p className="auth-intro">
              Une seule connexion, la première fois. Ensuite l'app fonctionne
              sans réseau.
            </p>
            <button type="button" className="auth-go"
                    onClick={() => { setErreur(null); setEtape('inscription') }}>
              Créer mon compte
            </button>
            <button type="button" className="auth-link"
                    onClick={() => { setErreur(null); setEtape('connexion') }}>
              J'ai déjà un compte
            </button>
          </div>
        )}

        {etape === 'inscription' && (
          <form className="auth-form" onSubmit={(e) => void inscrire(e)}>
            <label>
              <span>Prénom</span>
              <input type="text" autoComplete="given-name" required maxLength={MAX_NOM}
                     {...champ('first_name')} />
            </label>
            <label>
              <span>Nom</span>
              <input type="text" autoComplete="family-name" required maxLength={MAX_NOM}
                     {...champ('last_name')} />
            </label>
            <label>
              {/* Une DATE et non un âge : un âge est faux dès le lendemain de
                  l'anniversaire, et rien ne le remettrait à jour. */}
              <span>Date de naissance</span>
              <input type="date" required max={new Date().toISOString().slice(0, 10)}
                     {...champ('birthdate')} />
            </label>
            <label>
              <span>Numéro de téléphone</span>
              <input type="tel" inputMode="tel" autoComplete="tel" required
                     placeholder="034 12 345 67"
                     pattern={MOTIF_TELEPHONE} title={AIDE_TELEPHONE}
                     value={form.phone}
                     onChange={(event) => setForm((f) => (
                       { ...f, phone: formaterTelephone(event.target.value) }))} />
            </label>
            <label>
              <span>Email <em>(facultatif)</em></span>
              <input type="email" autoComplete="email" maxLength={255}
                     placeholder="pour retrouver ton compte" {...champ('email')} />
            </label>
            <label>
              {/* Huit caractères et non douze : on parle d'un public qui tape
                  sur un clavier de téléphone. Un minimum trop haut ne produit
                  pas des mots de passe plus sûrs, il en produit d'écrits sur un
                  papier. */}
              <span>Mot de passe <em>({MIN_MOT_DE_PASSE} caractères minimum)</em></span>
              <input type="password" autoComplete="new-password" required
                     minLength={MIN_MOT_DE_PASSE} {...champ('password')} />
            </label>

            <button type="submit" className="auth-go" disabled={envoi}>
              {envoi ? 'Envoi du code…' : 'Recevoir mon code'}
            </button>
            <button type="button" className="auth-link" onClick={() => setEtape('accueil')}>
              Retour
            </button>
          </form>
        )}

        {etape === 'connexion' && (
          <form className="auth-form" onSubmit={(e) => void seConnecter(e)}>
            <label>
              {/* Téléphone OU email : le serveur reconnaît les deux, et obliger
                  à choisir dans un menu serait une question de plus pour rien. */}
              <span>Numéro de téléphone ou email</span>
              <input type="text" autoComplete="username" required
                     placeholder="034 12 345 67"
                     pattern={MOTIF_IDENTIFIANT} title={AIDE_IDENTIFIANT}
                     value={identifiant}
                     onChange={(event) => setIdentifiant(formaterIdentifiant(event.target.value))} />
            </label>
            <label>
              <span>Mot de passe</span>
              <input type="password" autoComplete="current-password" required
                     value={motDePasse}
                     onChange={(event) => setMotDePasse(event.target.value)} />
            </label>
            <button type="submit" className="auth-go" disabled={envoi}>
              {envoi ? 'Connexion…' : 'Me connecter'}
            </button>
            <button type="button" className="auth-link"
                    onClick={() => { setErreur(null); setEtape('oubli') }}>
              Mot de passe oublié ?
            </button>
            <button type="button" className="auth-link" onClick={() => setEtape('accueil')}>
              Retour
            </button>
          </form>
        )}

        {etape === 'oubli' && (
          <form className="auth-form" onSubmit={(e) => void demanderReinitialisation(e)}>
            <p className="auth-intro">
              Indique ton numéro ou ton email : un code de vérification te sera
              envoyé pour choisir un nouveau mot de passe.
            </p>
            <label>
              <span>Numéro de téléphone ou email</span>
              <input type="text" autoComplete="username" required
                     placeholder="034 12 345 67"
                     pattern={MOTIF_IDENTIFIANT} title={AIDE_IDENTIFIANT}
                     value={identifiant}
                     onChange={(event) => setIdentifiant(formaterIdentifiant(event.target.value))} />
            </label>
            <button type="submit" className="auth-go" disabled={envoi}>
              {envoi ? 'Envoi du code…' : 'Recevoir un code'}
            </button>
            <button type="button" className="auth-link" onClick={() => setEtape('connexion')}>
              Retour
            </button>
          </form>
        )}

        {etape === 'nouveauMdp' && (
          <form className="auth-form" onSubmit={(e) => void reinitialiser(e)}>
            <p className="auth-intro">
              Un code à 6 chiffres a été envoyé à <strong>{identifiant}</strong>.
            </p>
            <label>
              <span>Code de vérification</span>
              <input type="text" inputMode="numeric" autoComplete="one-time-code"
                     className="auth-code tabular" required
                     pattern="\d{6}" maxLength={6} placeholder="000000"
                     value={code}
                     onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))} />
            </label>
            <label>
              <span>Nouveau mot de passe <em>({MIN_MOT_DE_PASSE} caractères minimum)</em></span>
              <input type="password" autoComplete="new-password" required
                     minLength={MIN_MOT_DE_PASSE}
                     value={nouveauMotDePasse}
                     onChange={(event) => setNouveauMotDePasse(event.target.value)} />
            </label>
            <button type="submit" className="auth-go"
                    disabled={envoi || code.length < 6 || nouveauMotDePasse.length < 8}>
              {envoi ? 'Validation…' : 'Choisir ce mot de passe'}
            </button>
            <button type="button" className="auth-link" disabled={resteAvantRenvoi > 0}
                    onClick={() => void renvoyerPourReinitialiser()}>
              {resteAvantRenvoi > 0
                ? `Renvoyer le code (${formatCompteARebours(resteAvantRenvoi)})`
                : 'Renvoyer le code'}
            </button>
            <button type="button" className="auth-link"
                    onClick={() => { setRenvoiApres(null); setEtape('accueil') }}>
              Recommencer
            </button>
          </form>
        )}

        {etape === 'code' && (
          <form className="auth-form" onSubmit={(e) => void verifier(e)}>
            <p className="auth-intro">
              Un code à 6 chiffres a été demandé pour <strong>{identifiant}</strong>.
            </p>
            <label>
              <span>Code de vérification</span>
              {/* `inputMode="numeric"` et non `type="number"` : ce dernier
                  mange les zéros de tête, et un code peut commencer par zéro. */}
              <input type="text" inputMode="numeric" autoComplete="one-time-code"
                     className="auth-code tabular" required
                     pattern="\d{6}" maxLength={6} placeholder="000000"
                     value={code}
                     onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))} />
            </label>
            <button type="submit" className="auth-go" disabled={envoi || code.length < 6}>
              {envoi ? 'Vérification…' : 'Ouvrir mon compte'}
            </button>
            <button type="button" className="auth-link" disabled={resteAvantRenvoi > 0}
                    onClick={() => void renvoyer()}>
              {resteAvantRenvoi > 0
                ? `Renvoyer le code (${formatCompteARebours(resteAvantRenvoi)})`
                : 'Renvoyer le code'}
            </button>
            <button type="button" className="auth-link"
                    onClick={() => { setRenvoiApres(null); setEtape('accueil') }}>
              Recommencer
            </button>
          </form>
        )}

        <p className="auth-foot">
          <Icon name="lock" size={13} />
          Tes données restent sur ton téléphone. La connexion ne sert qu'à les
          sauvegarder et à les retrouver si tu changes d'appareil.
        </p>
      </div>
    </div>
  )
}
